import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { Callbacks } from "./types";

/**
 * UniversalEntityPageCallbacksContext
 *
 * Contains all memoized callbacks and render functions:
 * - handleGroupingExport: Memoized grouping export handler
 * - renderSimpleTable: Memoized table renderer
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when data, handlers, or UI state changes.
 */
export type UniversalEntityPageCallbacksContextValue<T extends BaseEntity> = Callbacks<T>;

const UniversalEntityPageCallbacksContext = createContext<
  UniversalEntityPageCallbacksContextValue<any> | undefined
>(undefined);

export function UniversalEntityPageCallbacksProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageCallbacksContextValue<T>;
}) {
  return (
    <UniversalEntityPageCallbacksContext.Provider value={value}>
      {children}
    </UniversalEntityPageCallbacksContext.Provider>
  );
}

export function useUniversalEntityPageCallbacksContext<T extends BaseEntity>() {
  const context = useContext(UniversalEntityPageCallbacksContext);
  if (!context) {
    throw new Error(
      "useUniversalEntityPageCallbacksContext must be used within UniversalEntityPageCallbacksProvider"
    );
  }
  return context as UniversalEntityPageCallbacksContextValue<T>;
}
